/**
 * What is reachable, and what happens before anything is.
 *
 * The session gate wraps the whole authenticated tree rather than each route.
 * A gate per route is a gate that will be missing from the route added next,
 * and the failure mode of a missing one is a screen shown to somebody who
 * should not see it.
 */
import { useState } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router";

import { PageHeader } from "@/design-system/PageHeader";
import { Forbidden, Loading } from "@/design-system/states/States";
import { SignInPage } from "@/features/auth/SignInPage";
import { useSession } from "@/features/auth/useSession";
import { AcceptInvitationPage } from "@/features/identity/AcceptInvitationPage";
import { JoinPage } from "@/features/auth/JoinPage";
import { HelpPage } from "@/features/client/help/HelpPage";
import { StartPage } from "@/features/client/StartPage";
import { AccessPanel } from "@/features/identity/AccessPanel";
import { LegalGate } from "@/features/identity/LegalGate";
import { ClientsPage } from "@/features/identity/ClientsPage";
import { PlatformSystemPage } from "@/features/system/PlatformSystemPage";
import { readDetailed, ViewAsNotice, writeDetailed } from "@/features/auth/ViewAsNotice";
import { ClientHome } from "@/features/client/ClientHome";
import { ClientBilling, ClientSettings } from "@/features/client/ClientPages";
import { ClientDeals } from "@/features/client/ClientDeals";
import { AccountMoney, AccountTrading } from "@/features/identity/AccountPanel";
import { AccountAllocations } from "@/features/strategies/AccountAllocations";
import { BybitConnectionPanel } from "@/features/identity/BybitConnectionPanel";
import { FeeBalancePanel } from "@/features/identity/FeeBalancePanel";
import { PoolSelectionPanel } from "@/features/identity/PoolSelectionPanel";

import { CapitalPage } from "@/features/capital/CapitalPage";
import { OverviewPage } from "@/features/overview/OverviewPage";
import { ScannerPage } from "@/features/scanner/ScannerPage";
import { PlatformOverviewPage } from "@/features/platform/PlatformOverviewPage";
import { TelegramPanel } from "@/features/notifications/TelegramPanel";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { StrategiesPage } from "@/features/strategies/StrategiesPage";
import { SystemPage } from "@/features/system/SystemPage";
import { CyclesPage } from "@/features/cycles/CyclesPage";
import { MarketIntelligencePanel } from "@/features/ranking/MarketIntelligencePanel";
import { RankingPanel } from "@/features/ranking/RankingPanel";
import { useRanking } from "@/features/ranking/useRanking";
import { useRisk } from "@/features/risk/useRisk";
import { RiskPage } from "@/features/risk/RiskPage";
import { useOverview } from "@/shared/overview/useOverview";

import { ReleaseIndicator } from "./ReleaseIndicator";
import { AppShell } from "./AppShell";
import { MorePage } from "./MorePage";
import { SECTIONS } from "./navigation";
import { asConnection, asEnvironment } from "./TopBar";

const PLACEHOLDER = SECTIONS.filter(
  (section) => !section.available && section.path !== "/app",
);

function OverviewWithSources(): React.JSX.Element {
  const ranking = useRanking();
  const system = useRisk();
  return <OverviewPage ranking={<RankingPanel />} sources={{
    ranking: ranking.status === "ready" ? { status: "ready", data: ranking.ranking } : ranking,
    system: system.status === "ready" ? { status: "ready", data: system.system } : system,
  }} />;
}

export function AppRoutes(): React.JSX.Element {
  const session = useSession();
  const location = useLocation();
  // The administrator viewing a client's cabinet sees it as the client does,
  // and may open the detailed view; the client never has that switch.
  const [detailed, setDetailed] = useState(readDetailed);

  if (session.status === "checking") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background-base p-6">
        <Loading title="Проверяю сессию…" />
      </div>
    );
  }

  if (session.status === "signed-out") {
    // Rendered rather than redirected to. A redirect would need somewhere to
    // redirect back to, and a 401 arriving during that round trip is how a
    // sign-in loop starts.
    if (location.pathname === "/invite") return <AcceptInvitationPage />;
    if (location.pathname === "/reset-password") return <AcceptInvitationPage mode="reset" />;
    // Only by a client's invitation (owner, 2026-10-29).
    if (location.pathname.startsWith("/join/")) return <JoinPage />;
    return <SignInPage />;
  }

  if (session.status === "platform") {
    return <Routes>
      <Route path="/app" element={<AppShell platform login={session.login} accountId="Администратор платформы" roles={[]} connection="NOT_CONNECTED" environment="PLATFORM" />}>
        <Route index element={<PlatformOverviewPage />} />
        <Route path="clients" element={<ClientsPage adminLogin={session.login} />} />
        <Route path="system" element={<PlatformSystemPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="strategies" element={<StrategiesPage />} />
        <Route path="scanner" element={<ScannerPage market={<MarketIntelligencePanel testId="scanner-market" />} />} />
        <Route path="*" element={<PlatformWithoutAccount />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>;
  }

  const client = session.client && !(session.viewing !== null && detailed);

  return (
    <Routes>
      <Route path="/app" element={<Shell session={session} client={client} detailed={detailed} onDetailed={(next) => { writeDetailed(next); setDetailed(next); }} />}>
        {/* Composed here rather than inside the page: assembling two features
            is the app's job, and doing it in one of them would tie the two
            together for good. */}
        <Route
          index
          element={
            client ? (
              <ClientHome tenantId={session.tenantId} accountId={session.accountId}>
                <OverviewPage client />
              </ClientHome>
            ) : <OverviewWithSources />
          }
        />
        <Route path="billing" element={<ClientBilling />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="start" element={<StartPage tenantId={session.tenantId} accountId={session.accountId} roles={session.roles} />} />
        {/* The closed record moved into Циклы, where it is a tab rather
            than a screen of its own. Kept as a redirect because links to
            it exist — in the product, in notifications and in the owner's
            bookmarks — and a dead address teaches a reader that the
            section was removed. */}
        <Route path="history" element={<ClosedRecord />} />
        <Route path="capital" element={<CapitalPage />} />
        <Route path="risk" element={<RiskPage />} />
        <Route path="account" element={client ? <ClientSettings tenantId={session.tenantId} accountId={session.accountId} roles={session.roles} /> : <div className="flex flex-col gap-2">
          <PageHeader title="Мой счёт" description="Торгует ли счёт, куда идёт прибыль и что к нему подключено." />
          {/* Two questions side by side (owner, 2026-10-17): the left column
              is the account's trading and its connections, the right one its
              money — the profit share and how capital is used. */}
          {/* Two rows of two, so a phone reads them in order of importance:
              trading, profit, connections, capital. */}
          <div className="grid grid-cols-1 items-start gap-2 lg:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-2">
              <AccountTrading tenantId={session.tenantId} accountId={session.accountId} />
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <AccountMoney tenantId={session.tenantId} accountId={session.accountId} />
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <BybitConnectionPanel tenantId={session.tenantId} accountId={session.accountId} roles={session.roles} />
              <TelegramPanel owner={session.roles.includes("OWNER")} />
              <AccessPanel login={session.login} accountId={session.accountId} roles={session.roles} />
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <FeeBalancePanel />
              <PoolSelectionPanel owner={session.roles.includes("OWNER")} />
              <AccountAllocations tenantId={session.tenantId} accountId={session.accountId} roles={session.roles} />
            </div>
          </div>
        </div>} />
        <Route
          path="system"
          element={
            // Bybit and account access live on «Мой счёт»; here only the
            // machine's own readings.
            session.roles.includes("OPERATOR") ? <SystemPage /> : <Forbidden />
          }
        />
        <Route path="strategies" element={<StrategiesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="slots" element={client ? <ClientDeals /> : <CyclesPage />} />
        {/* The market block is composed here for the same reason it is on
            the Overview: it belongs to the ranking feature, and joining two
            features is the app's job. */}
        <Route
          path="scanner"
          element={<ScannerPage market={<MarketIntelligencePanel testId="scanner-market" />} />}
        />
        {PLACEHOLDER.map((section) => (
          <Route key={section.path} path={section.path.slice("/app/".length)} element={<Forbidden />} />
        ))}
        {/* A real destination, not a refusal. Strategies is not one of the
            four the mobile bar carries, and a section reachable only on a
            desktop is a section that does not exist for anybody on a phone. */}
        <Route path="more" element={<MorePage roles={session.roles} />} />
        <Route path="*" element={<Forbidden title="Такой страницы нет" />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

/**
 * The old address of the closed record, kept alive as a redirect.
 *
 * Links to it exist — in the product, in the owner's bookmarks, in messages
 * sent before the move — and a dead address teaches a reader that the section
 * was removed rather than relocated. The query travels with it: a link to one
 * symbol over one period is the whole value of such a link, and dropping the
 * parameters would land the reader on an unfiltered record and look like the
 * filter had stopped working.
 */
function ClosedRecord(): React.JSX.Element {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set("view", "closed");
  return <Navigate to={`/app/slots?${next.toString()}`} replace />;
}


/**
 * Connection and environment come from the overview. Its connection field is
 * the canonical scoped execution-health projection, separate from snapshot age.
 * Until it has answered, the bar says it does not know —
 * guessing "connected" on the one indicator that exists to report the
 * connection is worse than admitting it has not been told.
 *
 * Reading the overview here does not fetch it twice: the page asks for the
 * same thing under the same key, and one answer serves both.
 */


function Shell({
  session,
  client,
  detailed,
  onDetailed,
}: {
  readonly session: Extract<
    ReturnType<typeof useSession>,
    { status: "signed-in" }
  >;
  readonly client: boolean;
  readonly detailed: boolean;
  readonly onDetailed: (next: boolean) => void;
}): React.JSX.Element {
  const state = useOverview();
  const known = state.status === "ready" ? state.overview : null;

  // The same two queries the page already runs, under the same keys. Reading
  // them here costs no extra request and lets the bar name each feed's age
  // separately: the account ticks in seconds, the ranking on a quarter hour.
  const market = useRanking();
  const ranking = market.status === "ready" ? market.ranking : null;

  const sources = [
    known === null
      ? null
      : {
          name: "account",
          label: "Счёт",
          ageSeconds: known.age_seconds,
          stale: known.stale,
        },
    ranking?.age_seconds == null
      ? null
      : {
          name: "market",
          label: "Рынок",
          ageSeconds: ranking.age_seconds,
          stale: ranking.state === "STALE",
        },
  ].filter((source) => source !== null);

  return (
    <AppShell
      client={client}
      login={session.login}
      accountId={session.accountId}
      roles={session.roles}
      connection={state.status === "not-observed" ? "NOT_OBSERVED" : asConnection(known?.connection)}
      environment={state.status === "not-observed" ? "NONE" : asEnvironment(known?.environment)}
      equity={known?.equity}
      risk={known?.portfolio_risk}
      sources={sources}
      // The client's own sign-in accepts the documents; a platform view of a
      // client account reads only and is never asked to.
      notice={session.viewing === null ? <LegalGate /> : <ViewAsNotice name={session.viewing.name} detailed={detailed} onDetailed={onDetailed} />}
    />
  );
}

function PlatformWithoutAccount(): React.JSX.Element {
  return <section className="space-y-4" aria-label="Платформа без торгового счёта">
    <h1 className="text-lg font-semibold">Администрирование платформы</h1>
    <p>Торговый счёт не выбран. Создайте или выберите счёт клиента.</p>
    <ReleaseIndicator />
  </section>;
}
